import { useEffect, useRef } from 'react';
import { Autocomplete, TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getVisiblePages, pageMatchesQuery, type PageEntry } from '@/config/pageRegistry';

const PageSearch: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // "/" keyboard shortcut to focus the search box
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filterOptions = (options: PageEntry[], { inputValue }: { inputValue: string }) => {
    const query = inputValue.toLowerCase();
    if (!query) return options;
    return options.filter((option) => pageMatchesQuery(option, query));
  };

  return (
    <Autocomplete<PageEntry, false, false, false>
      options={getVisiblePages()}
      getOptionLabel={(option) => option.label}
      filterOptions={filterOptions}
      onChange={(_e, value) => {
        if (value) {
          navigate(value.path);
          inputRef.current?.blur();
        }
      }}
      value={null}
      blurOnSelect
      clearOnBlur
      openOnFocus
      size="small"
      sx={{ width: 300 }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder='Search pages  ( / )'
          variant="outlined"
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default PageSearch;
